import { useConversationPanelToggle } from "@circle-react-uikit/CircleAIAppShell/hooks/useConversationPanelToggle";
import { DOM_IDS } from "@circle-react-uikit/CircleAIAppShell/Layout/defaults";
import { useCircleAIShellStore } from "@circle-react-uikit/CircleAIAppShell/store/circleAIShellStore";
import { selectHasActiveConversationPanelSurface } from "@circle-react-uikit/CircleAIAppShell/store/conversationPanelSurfaceSlice";
import { useIsCommunityAdmin } from "@circle-react/contexts/punditUserContext";
import { Button } from "@circleco/compass/components/Button";
import { Tooltip } from "@circleco/compass/components/Tooltip";
import type { ReactNode } from "react";

import { t } from "@/i18n-js/instance";

import { CanvasBreadcrumb } from "./CanvasBreadcrumb";
import { CanvasHistoryNav } from "./CanvasHistoryNav";
import {
  CANVAS_HEADER_ACTIONS_ID,
  CANVAS_HEADER_LEFT_ACTIONS_ID,
  CANVAS_HEADER_TITLE_ACTIONS_ID,
} from "./constants";

interface CanvasHeaderRowProps {
  breadcrumb?: ReactNode;
  rightActions?: ReactNode;
}

export const CanvasHeaderRow = ({
  breadcrumb,
  rightActions,
}: CanvasHeaderRowProps) => {
  const isAdmin = useIsCommunityAdmin();
  const { isOpen: isPanelOpen, toggle: togglePanel } =
    useConversationPanelToggle();
  const isDesignPanelOpen = useCircleAIShellStore(
    selectHasActiveConversationPanelSurface,
  );
  const isAiActive = isPanelOpen && !isDesignPanelOpen;
  const sparklesLabel = isAiActive
    ? t("circle_ai_shell.close_ai_canvas")
    : t("circle_ai_shell.open_ai_canvas");

  const handleAiToggle = () => {
    if (isDesignPanelOpen) {
      useCircleAIShellStore.getState().closeConversationPanelSurface();
      return;
    }
    togglePanel();
  };

  return (
    <div
      data-testid="circle-ai-shell-canvas-header"
      className="grid h-[52px] shrink-0 grid-cols-[1fr_auto_1fr] items-center gap-2 px-4"
    >
      <div className="flex items-center gap-2 justify-self-start">
        {isAdmin && (
          <Tooltip content={sparklesLabel} side="bottom">
            <Button
              startIcon={isAiActive ? "sparkle-filled" : "sparkle"}
              variant="outline"
              size="sm"
              onClick={handleAiToggle}
              data-testid="circle-ai-shell-canvas-header-ai-toggle"
              data-circle-ai-toggle="canvas-header"
              data-popup-open={isAiActive || undefined}
              aria-label={sparklesLabel}
              aria-expanded={isAiActive}
              aria-pressed={isAiActive}
              aria-controls={DOM_IDS.conversationPanelBody}
            >
              {t("circle_ai_dock.primary.circle_ai")}
            </Button>
          </Tooltip>
        )}
        <CanvasHistoryNav />
        <div
          id={CANVAS_HEADER_LEFT_ACTIONS_ID}
          className="flex items-center gap-2"
        />
      </div>
      <div className="flex min-w-0 items-center gap-2 overflow-hidden">
        {breadcrumb ?? <CanvasBreadcrumb />}
        <div
          id={CANVAS_HEADER_TITLE_ACTIONS_ID}
          className="flex shrink-0 items-center gap-2"
        />
      </div>
      <div className="flex items-center gap-2 justify-self-end">
        {rightActions}
        <div
          id={CANVAS_HEADER_ACTIONS_ID}
          className="flex items-center gap-2"
        />
      </div>
    </div>
  );
};

CanvasHeaderRow.displayName = "CanvasHeaderRow";
